import { Component, forwardRef, input, model, signal, inject, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { LabelComponent } from "../label/label.component";
import { ValidationOutputComponent } from "../validation-output/validation-output.component";
import { CountryCodeInfo, ExtendedPhoneNumber, PhoneNumberService } from 'src/app/services/phone-number.service';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [LabelComponent, ValidationOutputComponent, FormsModule]
})
export class PhoneInputComponent implements ControlValueAccessor, AfterViewInit {
  phoneNumberService = inject(PhoneNumberService);

  label = input<string>();
  htmlFor = input<string>();
  errorMessage = model<string>();
  successMessage = input<string>();
  note = input<string>();
  isRequired = input<boolean>(true);
  inputStyleClass = input<string>('');
  disabled = model<boolean>(false);
  defaultCountry = '1';
  placeholder = input<boolean>(true);

  cursorPosition = signal<number>(0);
  value = model<ExtendedPhoneNumber | undefined>();
  nationalFormatted = signal<string | undefined>('');
  countryCallingCode = signal<string | undefined>(undefined);
  exampleNumber = signal<string>('');
  guessedCountryCodeInfo = signal<CountryCodeInfo | undefined>(undefined);

  ngAfterViewInit() {
    this.guessCallingCode();
  }

  onChange = (value: string | null) => { };
  onTouched = () => { };

  writeValue(value: string | null | undefined) {
    value = value?.trim();
    if (value?.startsWith('+')) {
      const phoneNumber = this.phoneNumberService.formatFromE164String(value);
      this.value.set({ phoneNumber });
      this.nationalFormatted.set(phoneNumber?.formatNational());
      this.countryCallingCode.set(phoneNumber?.countryCallingCode);
      this.onChange(value);
    } else {
      this.formatPhoneNumber(value, this.countryCallingCode() ?? this.defaultCountry);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  countryCodeChanged(event: Event) {
    const el = event.target as HTMLInputElement;
    const value = el.value;
    this.onTouched();
    this.formatPhoneNumber(this.value()?.phoneNumber?.nationalNumber, value);
    const isCallingCodeValid = this.phoneNumberService.isCountryCallingCodeValid(value);
    if (isCallingCodeValid) {
      this.countryCallingCode.set(`+${value}`);
    }
  }

  formatPhoneNumber(nationalNumber: string | null | undefined, countryCode: string = this.defaultCountry) {
    if (!nationalNumber) {
      this.value.set(undefined);
      this.onChange(null);
      return;
    }
    countryCode = countryCode.replace('+', '');
    const partialFormat = this.phoneNumberService.formatPartial(nationalNumber, countryCode ?? undefined);
    if (!partialFormat) return;
    this.value.set(partialFormat);
    this.nationalFormatted.set(partialFormat?.phoneNumber?.formatNational() ?? undefined);
    const callingCode = partialFormat?.phoneNumber?.countryCallingCode;
    this.countryCallingCode.set(callingCode ? `+${callingCode}` : callingCode);
    this.onChange(this.phoneNumberService.getRaw(this.value()?.phoneNumber));
  }

  onNationalInput(value: string): void {
    this.onTouched();
    this.formatPhoneNumber(value, this.countryCallingCode());
  }

  guessCallingCode() {
    const guess = this.phoneNumberService.tryGuessCountryCallingCode();
    this.guessedCountryCodeInfo.set(guess);
    this.countryCallingCode.set('+' + guess?.callingCode);
  }
}